/**
 * Sentry Error Monitoring
 *
 * Tracks errors and performance in production.
 */

import * as Sentry from '@sentry/react';

// Only initialize in production or if DSN is provided
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const isProd = import.meta.env.PROD;

/**
 * Initialize Sentry
 */
export function initSentry(): void {
  if (!SENTRY_DSN) {
    if (isProd) {
      console.warn('Sentry DSN not configured - error tracking disabled');
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: isProd ? 'production' : 'development',

    // Performance monitoring
    tracesSampleRate: isProd ? 0.1 : 1.0, // 10% in prod, 100% in dev

    // Session replay (optional)
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Filter out noisy errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'ResizeObserver loop',
      // Network errors (user's connection)
      'Failed to fetch',
      'NetworkError',
      'Load failed',
      // User cancelled
      'User rejected',
      'User denied',
    ],

    // Don't send PII
    beforeSend(event) {
      // Scrub wallet addresses from error messages
      if (event.message) {
        event.message = event.message.replace(
          /[1-9A-HJ-NP-Za-km-z]{32,44}/g,
          '[WALLET_ADDRESS]'
        );
      }
      return event;
    },

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
  });
}

/**
 * Capture an error with context
 */
export function captureError(
  error: Error | string,
  context?: Record<string, unknown>
): void {
  if (!SENTRY_DSN) {
    console.error('Error:', error, context);
    return;
  }

  if (typeof error === 'string') {
    Sentry.captureMessage(error, {
      level: 'error',
      extra: context,
    });
  } else {
    Sentry.captureException(error, {
      extra: context,
    });
  }
}

/**
 * Set user context (wallet address)
 */
export function setUserContext(walletAddress: string | null): void {
  if (!SENTRY_DSN) return;

  if (walletAddress) {
    Sentry.setUser({
      id: walletAddress.slice(0, 8) + '...' + walletAddress.slice(-4),
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>
): void {
  if (!SENTRY_DSN) return;

  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
}

/**
 * Start a performance transaction
 */
export function startTransaction(
  name: string,
  op: string
): Sentry.Span | undefined {
  if (!SENTRY_DSN) return undefined;

  return Sentry.startInactiveSpan({ name, op });
}

// Re-export ErrorBoundary for convenience
export { ErrorBoundary } from '@sentry/react';
